import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login } from '../reducer/loginSlice';
import { AuthApis } from '../apis/authApis';

const useAutoLogout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userLoginData = useSelector((state: any) => state.data.login.value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isLoggedInRef = useRef(false);

  // TEST DURATIONS - Use these for testing (logout in 2 minutes)
  // const TIMEOUT_DURATION = 2 * 60 * 1000; // 2 minutes for testing
  // const WARNING_DURATION = 1 * 60 * 1000; // 1 minute warning for testing
  
  // PRODUCTION DURATIONS - Uncomment these for production
  const TIMEOUT_DURATION = 20 * 60 * 1000; // 20 minutes
  const WARNING_DURATION = 18 * 60 * 1000; // 18 minutes

  const logout = useCallback(() => {
    // console.log('🚪 Auto-logout triggered');
    
    // Clear timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }

    // Set flag to prevent multiple logouts
    isLoggedInRef.current = false;

    AuthApis.logout('').then(() => {
      dispatch(login([]));
      navigate('/login');
      alert('You have been logged out due to inactivity.');
      window.location.reload();
    }).catch((error) => {
      console.log('Logout error:', error);
      // Force logout even if API call fails
      dispatch(login([]));
      navigate('/login');
      alert('You have been logged out due to inactivity.');
      window.location.reload();
    });
  }, [dispatch, navigate]);

  const showWarning = useCallback(() => {
    console.log('⚠️ Showing inactivity warning');
    
    const userConfirmed = window.confirm(
      'You will be logged out in 1 minute due to inactivity. Click OK to stay logged in.'
    );
    
    if (userConfirmed) {
      console.log('✅ User chose to stay logged in');
      resetTimer();
    } else {
      console.log('❌ User chose to logout or closed dialog');
      logout();
    }
  }, [logout]);

  const resetTimer = useCallback(() => {
    // console.log('🔄 Resetting inactivity timer');
    
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }

    // Only set timers if user is logged in
    if (userLoginData?.token && isLoggedInRef.current) {
      // console.log('⏰ Setting new timers - Warning in 1min, Logout in 2min');
      
      // Set warning timer
      warningTimeoutRef.current = setTimeout(() => {
        showWarning();
      }, WARNING_DURATION);
      
      // Set logout timer
      timeoutRef.current = setTimeout(() => {
        logout();
      }, TIMEOUT_DURATION);
    }
  }, [userLoginData?.token, showWarning, logout, WARNING_DURATION, TIMEOUT_DURATION]);

  // Reset timer on any user activity
  const resetTimerOnActivity = useCallback(() => {
    if (isLoggedInRef.current) {
      // console.log('🎯 User activity detected, resetting timer');
      resetTimer();
    }
  }, [resetTimer]);

  useEffect(() => {
    // Update login status
    const isCurrentlyLoggedIn = !!(userLoginData?.token);
    
    // console.log('👤 Login status changed:', isCurrentlyLoggedIn);
    // console.log('📊 User data:', userLoginData);

    if (!isCurrentlyLoggedIn) {
      // Clear timers if user is not logged in
      // console.log('🔴 User not logged in, clearing timers');
      isLoggedInRef.current = false;
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }
      return;
    }

    // User is logged in
    isLoggedInRef.current = true;
    // console.log('🟢 User is logged in, setting up auto-logout (TEST MODE: 2min timeout)');

    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
      'touchmove',
      'focus',
      'blur'
    ];

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, resetTimerOnActivity, true);
    });

    // Initialize timer
    resetTimer();

    // Cleanup function
    return () => {
      // console.log('🧹 Cleaning up auto-logout');
      
      events.forEach((event) => {
        document.removeEventListener(event, resetTimerOnActivity, true);
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }
    };
  }, [userLoginData?.token, resetTimer, resetTimerOnActivity]);

  // Debug: log current status every 30 seconds
  useEffect(() => {
    const debugInterval = setInterval(() => {
      if (isLoggedInRef.current) {
        // console.log('🔄 Auto-logout status: Active, user logged in (TEST MODE)');
      }
    }, 30000); // Log every 30 seconds

    return () => clearInterval(debugInterval);
  }, []);

  return null;
};

export default useAutoLogout;